const axios = require('axios').default;

import ApiService from '../../src/services/apiService';

const instance = axios.create({
  baseURL: `${process.env.VUE_APP_N1_API_PROTOCOL}://${process.env.VUE_APP_N1_API_HOST}${process.env.VUE_APP_N1_API_PATH}`,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    // requires Cache-Control for IE11 GET requests, otherwise they are not fetched again
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
  withCredentials: true,
});
instance.defaults.cache = false;
instance.interceptors.request.use(function (config) {
  config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
  return config;
});

// multipart/form-data
const instanceMultipart = axios.create({
  baseURL: `${process.env.VUE_APP_N1_API_PROTOCOL}://${process.env.VUE_APP_N1_API_HOST}${process.env.VUE_APP_N1_API_PATH}`,
  timeout: 10000,
  withCredentials: true,
  crossDomain: true,
});
instanceMultipart.interceptors.request.use(function (config) {
  config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
  return config;
});

export default {
  // #### ACCOUNTS
  accountsFetchPlain() {
    return instance.get('accounts/plain').then((res) => res.data);
  },
  accountsGet(id) {
    return instance.get(`account/${id}`).then((res) => res.data);
  },

  // #### APPLICATIONS
  applicationsFetchPlain() {
    return instance.get('applications/plain').then((res) => res.data);
  },
  applicationsFetch(page, limit, sort, order) {
    return instance.get(`applications?page=${page}&limit=${limit}&order=${order}&sort=${sort}`).then((res) => res.data);
  },
  applicationsFetchByAccount(accountIds, page, limit, sort, order) {
    if ((accountIds || []).length === 0) {
      return this.applicationsFetch(page, limit, sort, order);
    }
    let accountIdsQueryString = (accountIds || []).join(',');
    return instance
      .get(`account/${accountIdsQueryString}/applications?page=${page}&limit=${limit}&order=${order}&sort=${sort}`)
      .then((res) => res.data);
  },
  applicationsFind(searchItem, accountIds, page, limit, sort, order) {
    return instance
      .get(`application/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`)
      .then((res) => res.data);
  },
  applicationsFindByAccount(accountIds, searchItem, page, limit, sort, order) {
    return instance
      .get(
        `account/${accountIds.join(
          ','
        )}/applications/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`
      )
      .then((res) => res.data);
  },
  applicationsCreate(data) {
    return instance.post('application', data).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'application',
          action: 'create',
          title: data.title,
          id: res.data.data.application.id,
        });
      }

      return res.data;
    });
  },
  applicationsUpdate(id, data) {
    return instance.put(`application/${id}`, data).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'application',
          action: 'update',
          title: data.title,
          id: data.id,
        });
      }

      return res.data;
    });
  },
  applicationsDelete(data) {
    return instance.delete(`application/${data.id}`).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'devicetype',
          action: 'delete',
          title: data.title,
          id: data.id,
        });
      }

      return res.data;
    });
  },
  applicationGet(id) {
    return instance.get(`application/${id}`).then((res) => res.data);
  },
  applicationDevices(applicationId, page, limit, sort, order) {
    return instance
      .get(`application/${applicationId}/devices?page=${page}&limit=${limit}&order=${order}&sort=${sort}`)
      .then((res) => res.data);
  },
  applicationDevicesFind(applicationId, searchItem, page, limit, sort, order) {
    return instance
      .get(
        `application/${applicationId}/devices/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`
      )
      .then((res) => res.data);
  },
  getApplicationDevicesParsedPackets(applicationId, data) {
    return instance.post(`/application/${applicationId}/devices/parsed-packets`, data).then((res) => res);
  },
  getApplicationMetricsReporting(applicationId, data) {
    return instance.post(`application/${applicationId}/metrics/reporting`, data).then((res) => res.data);
  },
  getApplicationMetricsSignal(applicationId) {
    return instance.get(`application/${applicationId}/metrics/signal`).then((res) => res.data);
  },
  getApplicationMetricsHistory(applicationId) {
    return instance.get(`application/${applicationId}/metrics/history`).then((res) => res.data);
  },
  getApplicationMetricsPacketsDaily(applicationId) {
    return instance.get(`application/${applicationId}/metrics/packets/daily`).then((res) => res.data);
  },
  getApplicationMetricsPacketsHourly(applicationId) {
    return instance.get(`application/${applicationId}/metrics/packets/hourly`).then((res) => res.data);
  },
  getApplicationCustomDevices(applicationId) {
    return instance.get(`application/${applicationId}/devices/custom`).then((res) => res.data);
  },
  getApplicationDevicesBatteryStatus(applicationId) {
    return instance.get(`application/${applicationId}/devices/battery-status`).then((res) => res.data);
  },
  getApplicationDevicesPlain(applicationId) {
    return instance.get(`/application/${applicationId}/devices/plain`).then((res) => res.data);
  },
  getApplicationFilteredDevices(applicationId, data) {
    return instance.post(`application/${applicationId}/filtered/devices`, data).then((res) => res.data);
  },
  getBatteryLevelFilteredDevices(applicationId, data) {
    return instance.post(`application/${applicationId}/filtered/devices/battery-status`, data).then((res) => res.data);
  },
  applicationsFetchDeviceInfoFromSource(data) {
    return instance.post('application/device-info-from-source', data).then((res) => res.data);
  },

  // #### CONSUMERS
  consumersFetch(page, limit, sort, order) {
    return instance.get(`consumers?page=${page}&limit=${limit}&order=${order}&sort=${sort}`).then((res) => res.data);
  },
  consumersFind(searchItem, page, limit, sort, order) {
    return instance
      .get(`consumer/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`)
      .then((res) => res.data);
  },
  consumersGet(id) {
    return instance.get(`consumer/${id}`).then((res) => res.data);
  },
  consumersGetMQTTdata(id) {
    return instance.get(`consumer/${id}/mqtt`).then((res) => res.data);
  },
  consumersCreate(data) {
    return instance.post('consumer', data).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'consumer',
          action: 'create',
          title: data.title,
          id: res.data.data.consumer.id,
        });
      }

      return res.data;
    });
  },
  consumersTestJSONTrafo(data) {
    return instance.post('consumer/transformation', data).then((res) => res.data);
  },
  consumersUpdate(id, data) {
    return instance.put(`consumer/${id}`, data).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'consumer',
          action: 'update',
          title: data.title,
          id: id,
        });
      }

      return res.data;
    });
  },
  consumersDelete(data) {
    return instance.delete(`consumer/${data.id}`).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'consumer',
          action: 'delete',
          title: data.title,
          id: data.id,
        });
      }

      return res.data;
    });
  },
  consumersGetMetrics(externalId, page, limit, sort, order) {
    return instance
      .get(`IEC104/metrics/${externalId}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`)
      .then((res) => res.data);
  },
  consumersFetchByAccount(accountIds, page, limit, sort, order) {
    if ((accountIds || []).length === 0) {
      return this.consumersFetch(page, limit, sort, order);
    }
    let accountIdsQueryString = (accountIds || []).join(',');
    return instance
      .get(`account/${accountIdsQueryString}/consumers?page=${page}&limit=${limit}&order=${order}&sort=${sort}`)
      .then((res) => res.data);
  },
  consumersFindByAccount(accountIds, searchItem, page, limit, sort, order) {
    return instance
      .get(
        `account/${accountIds.join(
          ','
        )}/consumers/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`
      )
      .then((res) => res.data);
  },

  // #### DATASOURCES
  dataSourcesFetchPlain() {
    return instance.get('datasources/plain').then((res) => res.data);
  },
  dataSourcesFetch(searchItem, page, limit, sort, order) {
    let promise = undefined;
    if ((searchItem || '').trim() === '') {
      promise = instance.get(`datasources?page=${page}&limit=${limit}&order=${order}&sort=${sort}`);
    } else {
      promise = instance.get(`datasource/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`);
    }
    return promise.then((res) => res.data);
  },
  dataSourcesCreate(data) {
    return instance.post('datasource', data).then((res) => res.data);
  },
  dataSourcesUpdate(id, data) {
    return instance.put(`datasource/${id}`, data).then((res) => res.data);
  },
  dataSourcesDelete(id) {
    return instance.delete(`datasource/${id}`).then((res) => res.data);
  },
  dataSourceFetchById(id) {
    return instance.get(`datasource/${id}`).then((res) => res.data);
  },

  // #### DEVICES
  devicesFetch(page, limit, sort, order) {
    return instance.get(`devices?page=${page}&limit=${limit}&order=${order}&sort=${sort}`).then((res) => res.data);
  },
  devicesFetchByAccount(accountIds, page, limit, sort, order, searchItem) {
    if ((accountIds || []).length === 0) {
      if (!searchItem || searchItem.trim() === '') {
        return this.devicesFetch(page, limit, sort, order);
      } else {
        return this.devicesFind(searchItem, page, limit, sort, order);
      }
    }
    let queryString = `account/${accountIds.join(',')}/devices`;
    if (searchItem && searchItem.trim() !== '') {
      queryString += `/find/${searchItem}`;
    }
    queryString += `?page=${page}&limit=${limit}&order=${order}&sort=${sort}`;
    return instance.get(queryString).then((res) => res.data);
  },
  devicesFindByAccount(accountIds, searchItem, page, limit, sort, order) {
    return instance
      .get(
        `account/${accountIds.join(
          ','
        )}/devices/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`
      )
      .then((res) => res.data);
  },
  devicesFind(searchItem, page, limit, sort, order) {
    return instance
      .get(`devices/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`)
      .then((res) => res.data);
  },
  deviceGetDetails(id) {
    return instance.get(`device/${id}`).then((res) => res.data);
  },
  devicesCreate(data) {
    return instance.post('devices', data).then((res) => res);
  },
  deviceCreate(data) {
    return instance.post('device/remote', data).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'device',
          action: 'create',
          title: res.data.data.device.title,
          id: res.data.data.device.id,
        });
      }

      return res.data;
    });
  },
  devicesUpdate(data) {
    return instance.put('devices', data).then((res) => res.data);
  },
  deviceUpdate(id, data) {
    return instance.put(`device/${id}`, data).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'device',
          action: 'update',
          title: data.title,
          id: id,
        });
      }

      return res.data;
    });
  },
  devicesDelete(ids) {
    return instance.delete(`devices/${ids}`).then((res) => res.data);
  },
  deviceDelete(data) {
    return instance.delete(`device/${data.id}`).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'device',
          action: 'delete',
          title: data.title,
          id: data.id,
        });
      }
      return res.data;
    });
  },
  deviceDeleteFromActility(id) {
    return instance.delete(`device/remote/${id}`).then((res) => res.data);
  },
  devicesDeleteFromActility(ids) {
    return instance.delete(`devices/remote/${ids}`).then((res) => res.data);
  },
  deviceFetchPackets(id, from, offset) {
    return instance.get(`device/${id}/packets/${from}/${offset}`).then((res) => res.data);
  },
  deviceFetchStatus(id) {
    return instance.get(`device/${id}/remote-status`).then((res) => res.data);
  },
  deviceFetchParsedPackets(id, data) {
    return instance.post(`device/${id}/parsed-packets`, data).then((res) => res);
  },
  fetchDevicePacketsMetrics(id, data) {
    return instance.get(`device/${id}/metrics/packets/${data}`).then((res) => res.data);
  },
  fetchDevicePacketsStatistics(id) {
    return instance.get(`device/${id}/packets-statistics`).then((res) => res.data);
  },
  fetDeviceTransmissionMetrics(id, data) {
    return instance.post(`device/${id}/metrics/transmission`, data).then((res) => res.data);
  },
  devicesParserVariables() {
    return instance.get('devices/parser-variables').then((res) => res.data);
  },
  deviceSendDownlink(id, data) {
    return instance.post(`device/remote/${id}/packet`, data).then((res) => res.data);
  },
  deviceSendDownlinkToActility(id, data) {
    return instance.post(`device/actility/${id}/packet`, data).then((res) => res.data);
  },

  // #### Device Monitoring
  deviceMonitoring(range, accountId) {
    return instance.post('/device-monitoring', { range, accountId }, { timeout: 50000 }).then((response) => {
      return response.data;
    });
  },

  // #### DEVICETYPES
  deviceTypesFetch(page, limit, sort, order) {
    return instance.get(`devicetypes/?page=${page}&limit=${limit}&order=${order}&sort=${sort}`).then((res) => {
      return res.data;
    });
  },
  deviceTypesFind(searchItem, certified, usedInAccount, page, limit, sort, order) {
    return instance
      .get(
        `devicetype/${certified}/${usedInAccount}/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`
      )
      .then((res) => {
        // NOTE: normalizing results as using `searchItem` returns the data as `data.devicetype` not `data.devicetypes`
        if (searchItem) {
          res.data.data.devicetypes = res.data.data.devicetype;
          delete res.data.data.devicetype;
        }
        return res.data;
      });
  },
  deviceTypesGetPlain() {
    return instance.get('devicetypes/plain?').then((res) => {
      return res.data;
    });
  },
  deviceTypesGetCopy(id) {
    return instance.get(`devicetype/${id}/copy`).then((res) => {
      return res.data;
    });
  },
  deviceTypesValidateCopy(data) {
    return instance
      .put('devicetype/copy', {
        info: data,
      })
      .then((res) => {
        return res.data;
      });
  },
  deviceTypesCreate(data) {
    // return devicetypes.create(data);
    return instance.post('devicetype', data).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'devicetype',
          action: 'create',
          title: data.title,
          id: res.data.data.devicetype.id,
        });
      }

      return res.data;
    });
  },
  deviceTypesUpdate(id, data, entityChange) {
    return instance.put(`devicetype/${id}`, data).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'devicetype',
          action: 'update',
          title: data.title,
          id: data.id,
          entityChange: entityChange,
        });
      }

      return res.data;
    });
  },
  deviceTypesDelete(data) {
    return instance.delete(`devicetype/${data.id}`).then((res) => {
      if (res.status === 200) {
        ApiService.instance.post('activityLog/createEntry', {
          type: 'devicetype',
          action: 'delete',
          title: data.title,
          id: data.id,
        });
      }

      return res.data;
    });
  },
  deviceTypeGetDetails(id) {
    return instance.get(`devicetype/${id}`).then((res) => res.data);
  },
  deviceTypeFetchActivity(id) {
    return instance.get(`activity/devicetype/${id}/all`).then((res) => res.data);
  },
  getDeviceTypeDevices(id, searchItem, page, limit, sort, order) {
    return instance
      .get(`devicetype/${id}/devices/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`)
      .then((res) => res.data);
  },
  deviceTypeFetchPlain() {
    return instance.get('devicetypes/plain').then((res) => res.data);
  },
  deviceTypesParsePayload(data) {
    return instance.post('devicetype/parse', data).then((res) => res.data);
  },

  // #### ORIGINATORS
  originatorsFetch(searchItem, page, limit, sort, order) {
    let promise = undefined;
    if ((searchItem || '').trim() === '') {
      promise = instance.get(`originators?page=${page}&limit=${limit}&order=${order}&sort=${sort}`);
    } else {
      promise = instance.get(`originator/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`);
    }
    return promise.then((res) => res.data);
  },
  originatorsCreate(data) {
    return instance.post('originator', data).then((res) => res.data);
  },
  originatorsUpdate(id, data) {
    return instance.put(`originator/${id}`, data).then((res) => res.data);
  },
  originatorsDelete(id) {
    return instance.delete(`originator/${id}`).then((res) => res.data);
  },
  // originatorsFetchPlain(limit) {
  //   return instance.get(`originators?limit=${limit}`).then((res) => res.data);
  // },
  originatorsFetchPlain() {
    return instance.get('originators/plain').then((res) => res.data);
  },
  originatorsGetDevices(id) {
    return instance.get(`originator/${id}/devices`).then((res) => res.data);
  },
  fetchActilityOptions(id) {
    return instance.get(`originator/${id}/actility-options`).then((res) => res.data);
  },
  originatorsFetchByAccount(searchItem, accountIds, page, limit, sort, order) {
    let promise = undefined;
    if ((searchItem || '').trim() === '') {
      promise = instance.get(
        `account/${accountIds.join(',')}/originators?page=${page}&limit=${limit}&order=${order}&sort=${sort}`
      );
    } else {
      promise = instance.get(
        `account/${accountIds.join(
          ','
        )}/originators/find/${searchItem}?page=${page}&limit=${limit}&order=${order}&sort=${sort}`
      );
    }
    return promise.then((res) => res.data);
  },
  // #### UTILS
  uploadImage(imageData, entity = 'datasource', onProgressHandler) {
    let formData = new FormData();
    formData.append('file', imageData);
    return instanceMultipart
      .post(`upload/${entity}`, formData, {
        onUploadProgress: onProgressHandler,
        timeout: 50000,
      })
      .then((response) => {
        return response.data;
      });
  },
};
